import {
  Box,
  Button,
  Checkbox,
  Flex,
  Link,
  Select as NativeSelect,
  Tag,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RowSelectionState, createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { BodyOf, ResponseOf } from "../../core/api";
import { auth } from "../../core/auth";
import DataTable from "../../shared/components/DataTable/DataTable";
import useEndpointDataTable from "../../shared/components/DataTable/useEndpointDataTable";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import { useEntityLink } from "../../shared/hooks/useEntityLink";
import { OfficeId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import DailyNoteDetailsDrawer from "./components/DailyNoteDetailsDrawer";
import { INCIDENT_SUBTYPES, IncidentSubtype } from "./components/incidentSubtypes";
import { ResultCount } from "./dailyNotesShared";

type PageBody = BodyOf<"post", "./visit_daily_notes/sync_issues/page">;
type Issue = ResponseOf<"post", "./visit_daily_notes/sync_issues/page">["items"][number];
type ResubmitBody = BodyOf<"post", "./visit_daily_notes/sync_issues/resubmit">;

type Filters = {
  kind: PageBody["kind"];
  officeId: OfficeId | null;
};

const KIND_OPTIONS = [
  { label: "DoCare", value: "DOCARE" },
  { label: "Salesforce", value: "SALESFORCE" },
];

const STATUS_LABEL: Record<string, string> = {
  FAILED: "Failed",
  NEEDS_REVIEW: "Sent back for review",
};

const columnHelper = createColumnHelper<Issue>();

/** A row is one note and one kind of sync; the same note can fail both. */
function issueKey(issue: Issue): string {
  return `${issue.kind}-${issue.visitInstanceDailyNoteId}`;
}

function toResubmitItem(
  issue: Issue,
  subtype: IncidentSubtype | null
): ResubmitBody["items"][number] {
  return {
    kind: issue.kind,
    visitInstanceDailyNoteId: issue.visitInstanceDailyNoteId,
    incidentSubtype: issue.kind === "SALESFORCE" ? subtype : null,
  };
}

/**
 * The notes whose DoCare upload or Salesforce incident failed, with a way to send them again.
 *
 * A DoCare upload is listed once its retries are spent; an incident when it failed or Salesforce
 * sent it back. Anything still being retried is in progress and not listed. Sending again goes
 * through the same server function as the button on a single note, so the rules are the same;
 * the difference here is only that a nurse can clear a whole run of failures in one go.
 *
 * Sending again needs the signing right, as it does everywhere else; without it the list is
 * still readable.
 */
export default function DailyNoteSyncIssuesTab() {
  const { api, queries, mutations } = useApi();
  const entityLink = useEntityLink();
  const queryClient = useQueryClient();
  const toast = useToast();

  const offices = useQuery(queries.dailyNotes.reviewOffices());
  const canResend = auth.isAllowedTo("sign_visit_daily_note_attestation");

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [openNoteId, setOpenNoteId] = React.useState<Issue["visitInstanceDailyNoteId"] | null>(
    null
  );
  // The subtype to send a Salesforce incident as, where the nurse changed it.
  const [subtypeChoice, setSubtypeChoice] = React.useState<Record<string, IncidentSubtype>>({});

  const resubmit = useMutation({
    ...mutations.dailyNotes.resubmitSyncIssues(),
    onSuccess: ({ results }) => {
      const refused = results.filter((result) => !result.queued);
      const queued = results.length - refused.length;

      toast({
        title: queued === 0 ? "Nothing was queued" : `${queued} queued to send again`,
        description:
          refused.length === 0
            ? "They will go out shortly."
            : `${refused.length} could not be queued: ${refused
                .map((result) => result.reason ?? "refused")
                .join("; ")}`,
        status: queued === 0 ? "error" : refused.length === 0 ? "success" : "warning",
        position: "top-right",
        isClosable: true,
        duration: refused.length === 0 ? 5000 : 12000,
      });

      setRowSelection({});
      // Under the shared key, so the table and the dashboard counts both refresh.
      void queryClient.invalidateQueries({ queryKey: ["visitDailyNotes"] });
    },
    onError: (error) =>
      toast({
        title: "Could not send them again",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      }),
  });

  const columns = React.useMemo(
    () => [
      ...(canResend
        ? [
            columnHelper.display({
              id: "_select",
              header: ({ table }) => (
                <Checkbox
                  isChecked={table.getIsAllPageRowsSelected()}
                  isIndeterminate={table.getIsSomePageRowsSelected()}
                  onChange={table.getToggleAllPageRowsSelectedHandler()}
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  isChecked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  onClick={(event) => event.stopPropagation()}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor("kind", {
        header: "Type",
        cell: (info) => (
          <Tag colorScheme={info.getValue() === "DOCARE" ? "blue" : "orange"} size="sm">
            {info.getValue() === "DOCARE" ? "DoCare" : "Salesforce"}
          </Tag>
        ),
      }),
      columnHelper.accessor("patientName", {
        header: "Patient",
        cell: ({ row, getValue }) => (
          <Flex align="flex-start" direction="column">
            <Link
              color="blue.600"
              onClick={(event) => {
                event.stopPropagation();
                entityLink.open({
                  event,
                  entity: { type: "Patient", id: row.original.patientId },
                });
              }}
            >
              {getValue()}
            </Link>
            {row.original.caregiverId === null || row.original.caregiverName === null ? null : (
              <Text color="gray.500" fontSize="xs">
                Caregiver: {row.original.caregiverName}
              </Text>
            )}
          </Flex>
        ),
      }),
      columnHelper.accessor("visitStartTime", {
        header: "Visit",
        cell: (info) => (
          <Text whiteSpace="nowrap">{dateFormatter.toDateTime(info.getValue())}</Text>
        ),
      }),
      columnHelper.accessor("status", {
        header: "Problem",
        cell: ({ row }) => (
          <Box maxW="320px" whiteSpace="normal">
            <Text color="red.700" fontSize="sm" fontWeight="semibold">
              {STATUS_LABEL[row.original.status] ?? row.original.status}
            </Text>
            {row.original.error === null ? (
              <Text color="gray.500" fontSize="xs">
                Salesforce asked for a review: check the subtype and send it again.
              </Text>
            ) : (
              <Text color="gray.600" fontSize="xs">
                {row.original.error}
              </Text>
            )}
          </Box>
        ),
      }),
      columnHelper.display({
        id: "_attempts",
        header: "Attempts",
        cell: ({ row }) => (
          <Text whiteSpace="nowrap">
            {row.original.retryCount} of {row.original.maxRetries}
          </Text>
        ),
      }),
      columnHelper.accessor("lastAttemptAt", {
        header: "Last attempt",
        cell: (info) =>
          info.getValue() === null ? (
            <Text color="gray.500">—</Text>
          ) : (
            <Text whiteSpace="nowrap">{dateFormatter.toDateTime(info.getValue()!)}</Text>
          ),
      }),
      ...(canResend
        ? [
            columnHelper.display({
              id: "_actions",
              header: "",
              cell: ({ row }) => {
                const issue = row.original;
                const key = issueKey(issue);
                const stored = INCIDENT_SUBTYPES.find(
                  (subtype) => subtype === issue.incidentSubtype
                );
                const subtype = subtypeChoice[key] ?? stored ?? null;

                return (
                  <Flex align="center" gap={2} justify="flex-end">
                    {issue.status === "NEEDS_REVIEW" ? (
                      <NativeSelect
                        maxW="180px"
                        size="xs"
                        value={subtype ?? ""}
                        onChange={(event) => {
                          const chosen = INCIDENT_SUBTYPES.find((x) => x === event.target.value);
                          if (chosen !== undefined) {
                            setSubtypeChoice((previous) => ({ ...previous, [key]: chosen }));
                          }
                        }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {subtype === null ? <option value="">Choose a subtype</option> : null}
                        {INCIDENT_SUBTYPES.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </NativeSelect>
                    ) : null}
                    <Button
                      isLoading={resubmit.isPending}
                      size="xs"
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        resubmit.mutate({ items: [toResubmitItem(issue, subtype)] });
                      }}
                    >
                      Send again
                    </Button>
                  </Flex>
                );
              },
            }),
          ]
        : []),
    ],
    [canResend, entityLink, resubmit, subtypeChoice]
  );

  const { table, query, totalCount, filters, setFilter, metaButtonProps } = useEndpointDataTable({
    queryKey: ["visitDailyNotes", "syncIssues", "page"],
    columns: columns,
    columnVisiblity: {
      storage: { key: "visits/dailyNotes/syncIssues", version: 1 },
    },
    filters: {
      initialState: { kind: null, officeId: null } satisfies Filters as Filters,
    },
    rowSelection: {
      state: rowSelection,
      onRowSelectionChange: setRowSelection,
      getRowId: issueKey,
    },
    fetchPage: async (request) => {
      const body: PageBody = {
        kind: request.filters.kind,
        officeId: request.filters.officeId,
        limit: request.limit,
        offset: request.offset,
      };

      return await api.post("./visit_daily_notes/sync_issues/page", { body });
    },
    fetchAllForExport: async () => [],
  });

  // A selection describes the rows that were on screen; once a filter moves it does not.
  React.useEffect(() => {
    setRowSelection({});
  }, [filters]);

  const selected = table.getSelectedRowModel().rows.map((row) => row.original);

  const filterNode = (
    <Flex gap={3} wrap="wrap">
      <Select
        allowUnselect={true}
        label="Type"
        multiple={false}
        options={KIND_OPTIONS}
        value={filters.kind}
        onChange={(x) => setFilter("kind", (x as Filters["kind"]) ?? null)}
      />
      <Select
        allowUnselect={true}
        isLoading={offices.isPending}
        label="Office"
        multiple={false}
        options={(offices.data ?? []).map((office) => ({ label: office.name, value: office.id }))}
        value={filters.officeId}
        onChange={(x) => setFilter("officeId", x ?? null)}
      />
    </Flex>
  );

  const actionNode = canResend ? (
    <Button
      colorScheme="blue"
      isDisabled={selected.length === 0}
      isLoading={resubmit.isPending}
      onClick={() =>
        resubmit.mutate({
          items: selected.map((issue) =>
            toResubmitItem(issue, subtypeChoice[issueKey(issue)] ?? null)
          ),
        })
      }
    >
      Send selected again{selected.length === 0 ? "" : ` (${selected.length})`}
    </Button>
  ) : undefined;

  return (
    <Box>
      <Page.Header pb={2}>
        <Flex align="center" gap={3}>
          <Page.Title>Sync issues</Page.Title>
          <ResultCount count={totalCount} isLoading={query.isPending} noun="issue" />
        </Flex>
      </Page.Header>

      <Page.Content pt={1}>
        <DataTable
          actionNode={actionNode}
          filterNode={filterNode}
          isLoading={query.isPending}
          metaButtonProps={metaButtonProps}
          spacing="tight"
          table={table}
          onClickRow={(_event, row) => setOpenNoteId(row.original.visitInstanceDailyNoteId)}
        />
      </Page.Content>

      <DailyNoteDetailsDrawer
        visitInstanceDailyNoteId={openNoteId}
        onClose={() => setOpenNoteId(null)}
      />
    </Box>
  );
}
